import React, { useEffect, useRef } from 'react'
import { formatarDataOS } from '../../../../utils/date'
import { NotImported } from '../../NotInformat'
import styles from './osDateField.module.css'

type Props = {
  label: string
  icon?: string
  value?: string | null
  editable: boolean
  saving?: boolean
  onSave: (value: string) => void
}

const pad = (value: number) => String(value).padStart(2, '0')

/**
 * Converte a data da O.S (já deslocada +3h na tela de detalhes) para o valor de um
 * input datetime-local, no mesmo horário exibido por formatarDataOS.
 */
const toInputValue = (iso?: string | null) => {
  if (!iso) return ''
  const date = new Date(iso)
  if (isNaN(date.getTime())) return ''
  date.setHours(date.getHours() - 3)
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Data da O.S em destaque; quando editável, abre o calendário ao clicar. */
export function OSDateField({ label, icon, value, editable, saving, onSave }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
  }, [])

  // O seletor nativo dispara uma mudança por ajuste (dia, hora, minuto); salva só depois que o usuário para.
  const scheduleSave = (next: string) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => onSave(next), 900)
  }

  const openPicker = () => {
    const input = inputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null
    if (!input) return
    try {
      input.showPicker ? input.showPicker() : input.focus()
    } catch {
      input.focus()
    }
  }

  const content = (
    <>
      {icon && <i className={`bi ${icon} ${styles.icon}`} aria-hidden />}
      <span className={styles.label}>{label}</span>
      {value ? (
        <span className={styles.value}>{formatarDataOS(value)}</span>
      ) : editable ? (
        <span className={styles.placeholder}>Definir data</span>
      ) : (
        <NotImported className='fs-7 text-gray-400 fw-semibold mb-0' />
      )}
      {editable && (
        saving
          ? <span className='spinner-border spinner-border-sm ms-auto text-primary' />
          : <i className={`bi bi-pencil ${styles.edit}`} aria-hidden />
      )}
    </>
  )

  if (!editable) {
    return <div className={styles.field}>{content}</div>
  }

  return (
    <div
      className={`${styles.field} ${styles.editable}`}
      role='button'
      tabIndex={0}
      title={`Alterar data de ${label.toLowerCase()}`}
      onClick={openPicker}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          openPicker()
        }
      }}
    >
      {content}
      <input
        ref={inputRef}
        type='datetime-local'
        className={styles.hiddenInput}
        defaultValue={toInputValue(value)}
        key={value || 'empty'}
        tabIndex={-1}
        aria-hidden
        onChange={(event) => {
          if (event.target.value) scheduleSave(event.target.value)
        }}
      />
    </div>
  )
}
